import {
  Cart as MedusaCart,
  Order as MedusaOrder,
  StoreCartsRes,
  StoreGetProductsParams,
  StorePostCartsCartReq
} from '@medusajs/medusa'
import {
  PricedProduct,
  PricedShippingOption,
  PricedVariant
} from '@medusajs/medusa/dist/types/pricing'
import Medusa from '@medusajs/medusa-js'
import { Cart } from '@miles/domain/Store/Models/Cart/Cart'
import { CartParseService } from '@miles/domain/Store/Services/Cart/CartParseService'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { Array as A } from 'effect'
import pLimit from 'p-limit'
import { mutate } from 'swr'
import { v4 as UUIDv4 } from 'uuid'
import { GET_CART_SWR_KEY } from 'components/Hook/CartContext'
import {
  AttendeeId,
  CartId,
  VariantId
} from 'domain/models/ValueObject/EntityIds'
import { CartUtils } from 'services/api/utils/CartUtils'
import { getCookie } from 'services/cookies/helpers'
import {
  deleteFromLocalStorage,
  setInLocalStorage
} from 'services/localStorage/getOrSetLocalStorage'
import {
  AVOID_LINE_ITEM_MERGE_ID_KEY,
  KEY_CART_IN_LOCALSTORAGE,
  TRACKING_KEY_IN_COOKIE
} from 'store/constants'
import {
  medusaProductAndVariantsModelizer,
  medusaVariantModelizer
} from 'store/modelizers'
import { PeyceProduct, PeyceVariant } from 'store/types'

interface IAddItemInCart {
  cartId: string
  metadata?: Record<string, unknown> | undefined
  variantId: string
  quantity?: number
}

interface IUpdateItemInCart
  extends Pick<IAddItemInCart, 'cartId' | 'metadata' | 'quantity'> {
  lineItemId: string
}

interface IStorefrontApiClient {
  addItemInCart({
    cartId,
    metadata,
    variantId,
    quantity
  }: IAddItemInCart): Promise<void>
  completeCart(cartId: string): Promise<void>
  createNewCart(): Promise<Cart>
  createPaymentSession(cartId: string): Promise<string>
  deleteLineItemFromCart(cartId: string, lineItemId: string): Promise<void>
  getCartById(cartId: string): Promise<Cart>
  getProductsByIds(productIds: string[]): Promise<PeyceProduct[]>
  getProductsAndInventoriesByIds(
    productIds: string[],
    currency: string
  ): Promise<PeyceProduct[]>
  getVariantById(id: string): Promise<PeyceVariant>
  updateCart({
    cartId,
    payload
  }: {
    cartId?: string
    payload: StorePostCartsCartReq
  }): Promise<void>
  removeAttendeeFromCart(
    cartId: string,
    attendeeId: string
  ): Promise<AxiosResponse<any, any> | undefined>
  getPartnerVariants(
    attendeeIds: AttendeeId[],
    cartId: string
  ): Promise<PricedVariant[]>
}

const limit = pLimit(5)
const MAX_CHUNK = 100

class MedusaStorefrontClient implements IStorefrontApiClient {
  private instance: Medusa | null = null

  private creatingPaymentSessions: Promise<string> | null = null

  private get medusaClient(): Medusa {
    return this.instance ?? this.initMedusaClient()
  }

  private initMedusaClient() {
    return new Medusa({
      maxRetries: 3,
      baseUrl: process.env.NEXT_PUBLIC_MEDUSA_URL as string
    })
  }

  async getVariantById(id: string): Promise<PeyceVariant> {
    try {
      const variant = (await this.medusaClient.products.variants.retrieve(id))
        .variant
      return medusaVariantModelizer(variant)
    } catch {
      throw new Error('An error occured during cart retrieval or creation')
    }
  }

  async getVariantsByIds(
    cartId: string,
    ids: string[]
  ): Promise<PricedVariant[]> {
    if (ids.length === 0) {
      return []
    }
    const { variants } = await this.medusaClient.products.variants.list({
      id: ids,
      cart_id: cartId
    })
    return variants
  }

  async getCartShippingOption(cartId: string): Promise<{
    final: PricedShippingOption | undefined
    beforeDiscount: PricedShippingOption | undefined
  }> {
    const { shipping_options } =
      await this.medusaClient.shippingOptions.listCartOptions(cartId)

    return {
      final: shipping_options.find(
        (so) => so.data?.id === 'miles-republic-fulfillment-fees'
      ),
      beforeDiscount: shipping_options.find(
        (so) =>
          so.data?.id === 'miles-republic-fulfillment-fees-before-discount'
      )
    }
  }

  async createNewCart(): Promise<Cart> {
    try {
      const cart = (await this.medusaClient.carts.create()).cart
      setInLocalStorage(KEY_CART_IN_LOCALSTORAGE, cart.id)
      return CartParseService.parseCart(cart)
    } catch (e) {
      throw new Error('An error occured during cart creation')
    }
  }

  async addItemInCart({
    cartId,
    variantId,
    metadata,
    quantity = 1
  }: IAddItemInCart): Promise<void> {
    await this.medusaClient.carts.lineItems.create(cartId, {
      variant_id: variantId,
      quantity,
      metadata: {
        ...(metadata || {}),
        // this id is used to avoid line items merging
        [AVOID_LINE_ITEM_MERGE_ID_KEY]: UUIDv4()
      }
    })
  }

  async updateItemInCart({
    cartId,
    lineItemId,
    metadata,
    quantity = 1
  }: IUpdateItemInCart): Promise<void> {
    await this.medusaClient.carts.lineItems.update(cartId, lineItemId, {
      quantity,
      metadata
    })
  }

  async updateCart({
    cartId,
    payload
  }: {
    cartId: string
    payload: StorePostCartsCartReq
  }): Promise<void> {
    await this.medusaClient.carts.update(cartId, payload)
  }

  async getCartById(cartId: string): Promise<Cart> {
    const cart = await this.getCartOnlyById(cartId)
    return CartParseService.parseCart(cart)
  }

  async getCartOnlyById(cartId: string): Promise<StoreCartsRes['cart']> {
    const { cart } = await this.medusaClient.client.request(
      'GET',
      `/store/carts/${cartId}?expand=items.adjustments.discount.rule`
    )
    return cart as StoreCartsRes['cart']
  }

  async refreshCart(cartId: CartId) {
    await this.medusaClient.carts.deleteDiscount(
      cartId,
      'FAKE_DISCOUNT_ID_TO_REFRESH_CART'
    )
  }

  async addShippingMethod(
    cartId: string
  ): Promise<Omit<MedusaCart, 'refundable_amount' | 'refunded_total'>> {
    const { shipping_options } =
      await this.medusaClient.shippingOptions.listCartOptions(cartId)
    const shippingOption = shipping_options.find(
      (shipping_option) =>
        shipping_option.data?.id === 'miles-republic-fulfillment-fees'
    )
    if (shippingOption?.id === undefined) {
      throw new Error(
        'Could not find shipping option with id: miles-republic-fulfillment-fees'
      )
    }
    let retry = 3

    do {
      console.log(`Try adding shipping method to cart. ${retry} retries left.`)
      const { cart } = await this.medusaClient.carts.addShippingMethod(cartId, {
        option_id: shippingOption.id
      })
      if (cart.shipping_methods.length === 1) {
        console.log('Successfully updated the cart with new shipping method.')
        return cart
      } else {
        console.log('Cart does not have exactly 1 shipping method.')
        if (retry === 0) {
          throw new Error(
            'Could not get a cart with exactly one shipping method after 4 attempts.'
          )
        }
        retry--
      }
    } while (true)
  }

  /**
   * This method is stateful and MUST NOT be called server-side.
   * @param cartId
   * @returns
   */
  async createPaymentSession(cartId: string): Promise<string> {
    if (this.creatingPaymentSessions) {
      console.log('Already creating payment sessions... Skipping.')
      return this.creatingPaymentSessions
    }
    this.creatingPaymentSessions = (async () => {
      const parsedUtmzzCookie = JSON.parse(
        getCookie(TRACKING_KEY_IN_COOKIE) || '{}'
      )
      try {
        await this.updateCart({
          cartId,
          payload: { context: { utmzz: parsedUtmzzCookie } }
        })
        const paymentSession = (
          await this.medusaClient.carts.createPaymentSessions(cartId)
        ).cart.payment_session
        if (!paymentSession) {
          const { cart } = await this.medusaClient.carts.setPaymentSession(
            cartId,
            {
              provider_id: 'PeycePaymentProvider'
            }
          )
          return cart.payment_session?.data.client_secret as string
        }
        return paymentSession.data.client_secret as string
      } catch (err) {
        console.error({ err })
        throw new Error(
          `Unable to create payment session for cart with id : ${cartId}`
        )
      } finally {
        console.log('Releasing creating payment sessions lock.')
        this.creatingPaymentSessions = null
      }
    })()
    return this.creatingPaymentSessions
  }

  async completeCart(cartId: string) {
    try {
      await storefrontApiClient.addShippingMethod(cartId)
      await this.medusaClient.carts.complete(cartId)
      deleteFromLocalStorage(KEY_CART_IN_LOCALSTORAGE)
      mutate(GET_CART_SWR_KEY)
      await axios.post(
        `${process.env.NEXT_PUBLIC_MEDUSA_URL as string}/free-cart/confirm`,
        {
          cartId
        }
      )
    } catch {
      throw new Error(
        `An error occured during cart completion, with cartId: ${cartId}`
      )
    }
  }

  async getProductsByIds(productIds: string[]) {
    try {
      const products = (
        await this.medusaClient.products.list({
          id: productIds
        })
      ).products
      return products.map(medusaProductAndVariantsModelizer)
    } catch (err) {
      console.error({ err })
      throw new Error(
        `An error occured during products retrieval, with ids: ${productIds}`
      )
    }
  }

  async getProductsAndInventoriesByIds(productIds: string[], currency: string) {
    try {
      const productsWithInventories = (
        await axios.get<PricedProduct[]>(
          `${process.env.NEXT_PUBLIC_MEDUSA_URL as string}/products/inventory`,
          { params: { id: productIds.join(','), currency } }
        )
      ).data
      return productsWithInventories.map(medusaProductAndVariantsModelizer)
    } catch (err) {
      console.error({ err })
      throw new Error(
        `An error occured during products retrieval, with ids: ${productIds}`
      )
    }
  }

  async deleteLineItemFromCart(cartId: string, lineItemId: string) {
    try {
      const cart = await this.getCartById(cartId)
      if (cart && cart.completed_at) return
      await this.medusaClient.carts.lineItems.delete(cartId, lineItemId)
    } catch (err) {
      console.error({ err })
      throw new Error(
        `An error occured during line item deletion, with cartId: ${cartId} & lineItemId: ${lineItemId}`
      )
    }
  }

  async removeAttendeeFromCart(cartId: string, attendeeId: string) {
    const cart = await this.getCartById(cartId)
    if (cart && cart.completed_at) return

    return await axios.delete(
      `${
        process.env.NEXT_PUBLIC_MEDUSA_URL as string
      }/cart/${cartId}/attendee/${attendeeId}`
    )
  }

  async removeRacesFromCart(cartId: string, variantIds: VariantId[]) {
    const cart = await storefrontApiClient.getCartById(cartId)

    const removeAttendeeFromCartOps = variantIds.flatMap((variantId) =>
      cart.items
        .filter(CartUtils.isCartItemVariantSameAsRaceVariantId(variantId))
        .flatMap((item) =>
          item.type === 'SoloBib'
            ? [item.attendee.id]
            : item.attendees.map((attendee) => attendee.id)
        )
        .map((attendeeId) =>
          storefrontApiClient.removeAttendeeFromCart(cartId, attendeeId)
        )
    )
    await Promise.all(removeAttendeeFromCartOps)
  }

  async getMultipleCarts(
    cartIds: string[],
    orderIds: string[]
  ): Promise<{ data: (MedusaCart | MedusaOrder)[] }> {
    const url = `${
      process.env.NEXT_PUBLIC_MEDUSA_URL as string
    }/cart/get-multiple`

    const cartChunks = A.chunksOf(cartIds, MAX_CHUNK)
    const orderChunks = A.chunksOf(orderIds, MAX_CHUNK)

    const maxLen = Math.max(cartChunks.length, orderChunks.length)

    const pairs: { cartIds: string[]; orderIds: string[] }[] = Array.from(
      { length: maxLen },
      (_, i) => ({
        cartIds: cartChunks[i] ?? [],
        orderIds: orderChunks[i] ?? []
      })
    )

    const responses = await Promise.all(
      pairs.map(({ cartIds, orderIds }) =>
        limit(
          () =>
            axios.post(url, { cartIds, orderIds }) as Promise<{
              data: (MedusaCart | MedusaOrder)[]
            }>
        )
      )
    )

    return {
      data: responses.flatMap((r) => r.data)
    }
  }

  async addDiscount({
    cart,
    code
  }: {
    cart: Cart
    code: string
  }): Promise<Cart> {
    try {
      return CartParseService.parseCart(
        (
          await this.medusaClient.carts.update(cart.id, {
            discounts: [
              { code },
              ...cart.discounts
                .filter(({ metadata }) => !!metadata.type)
                .map(({ code }) => ({ code }))
            ]
          })
        ).cart
      )
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 404) {
          throw { message: 'NOT_FOUND' }
        } else if (error.response?.data?.message?.endsWith('is expired')) {
          throw { message: 'EXPIRED' }
        } else if (error.response?.data?.message?.endsWith('not valid yet')) {
          throw { message: 'NOT_VALID_YET' }
        } else if (
          error.response?.data?.message?.endsWith('maximum allowed times')
        ) {
          throw { message: 'DEPLETED' }
        }
      }
      throw error
    }
  }

  async removeDiscount({ cartId, code }: { cartId: string; code: string }) {
    try {
      await this.medusaClient.carts.deleteDiscount(cartId, code)
    } catch {
      throw new Error('An error occured when removing promocode in cart')
    }
  }

  async getCompetitionPassProduct(handle: StoreGetProductsParams) {
    try {
      return (await this.medusaClient.products.list(handle)).products
    } catch {
      throw new Error(
        `An error occured during product retrieval, with handle: ${handle}`
      )
    }
  }

  async getVariants(ids: VariantId[]): Promise<PricedVariant[]> {
    try {
      const { variants } = await this.medusaClient.products.variants.list({
        id: ids
      })

      return variants
    } catch {
      throw new Error('An error occured during cart retrieval or creation')
    }
  }
  async getPartnerVariants(attendeeIds: AttendeeId[], cartId: string) {
    const variants = await this.medusaClient.products.variants.list({
      title: attendeeIds,
      cart_id: cartId
    })

    return variants.variants
  }
}

export const storefrontApiClient = new MedusaStorefrontClient()
